/**
 * PublicArticles.tsx
 * Route : /articles
 * Recherche sémantique dans les codes de droit français (code_articles_qwen3)
 */

import { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { PublicHeader, PublicFooter, MCPBadge } from '@/components/jurisprudence';

const SITE_URL     = 'https://smartlawyer.ai';
const DISPLAY_STEP = 10;

// labelKey : clé i18n du libellé affiché ; query : requête envoyée à l'API (inchangée)
const QUICK_EXAMPLES = [
  { labelKey: 'articles.search.examples.civil_liability', query: 'responsabilité civile faute dommage' },
  { labelKey: 'articles.search.examples.dismissal',       query: 'licenciement cause réelle sérieuse procédure' },
  { labelKey: 'articles.search.examples.commercial_lease', query: 'bail commercial renouvellement droit au bail' },
  { labelKey: 'articles.search.examples.misuse_assets',   query: 'abus de biens sociaux usage contraire intérêts' },
  { labelKey: 'articles.search.examples.insolvency',      query: 'sauvegarde redressement liquidation judiciaire' },
];

// Codes fréquemment consultés
const FEATURED_CODES: { slug: string; labelKey: string }[] = [
  { slug: 'code-civil',                    labelKey: 'articles.codes.civil' },
  { slug: 'code-de-commerce',              labelKey: 'articles.codes.commerce' },
  { slug: 'code-du-travail',               labelKey: 'articles.codes.travail' },
  { slug: 'code-penal',                    labelKey: 'articles.codes.penal' },
  { slug: 'code-de-procedure-civile',      labelKey: 'articles.codes.proc_civile' },
  { slug: 'code-de-procedure-penale',      labelKey: 'articles.codes.proc_penale' },
  { slug: 'code-de-la-securite-sociale',   labelKey: 'articles.codes.securite_sociale' },
  { slug: 'code-monetaire-et-financier',   labelKey: 'articles.codes.monetaire_financier' },
];

interface ArticleResult {
  id: string;
  score: number;
  slug_code: string;
  slug: string;
  numero: string;
  code: string;
  texte: string;
  section_summary: string;
}

function ArticleCard({ r }: { r: ArticleResult }) {
  const { t } = useTranslation();
  const numero  = r.numero || '?';
  const code    = r.code || r.slug_code || '';
  const excerpt = r.section_summary || r.texte || '';
  const path    = r.slug_code && r.numero
    ? `/articles/${r.slug_code}/${encodeURIComponent(r.numero)}`
    : null;

  return (
    <div className="rounded-xl border border-border bg-card hover:border-primary/30 hover:shadow-sm transition-all p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-widest text-primary">
              Art. {numero}
            </span>
            <span className="text-[11px] text-muted-foreground truncate">{code}</span>
          </div>
          {excerpt && (
            <p className="text-[12px] text-muted-foreground leading-relaxed line-clamp-4">
              {excerpt}
            </p>
          )}
        </div>
        <div className="flex-shrink-0 text-[10px] text-muted-foreground/60 font-mono">
          {Math.round(r.score * 100)}%
        </div>
      </div>
      {path && (
        <div className="mt-3 pt-3 border-t border-border/50">
          <Link
            to={path}
            className="text-[12px] text-primary font-medium hover:underline"
          >
            {t('articles.read_article')}
          </Link>
        </div>
      )}
    </div>
  );
}

export default function PublicArticles() {
  const { t } = useTranslation();
  const [query, setQuery]               = useState('');
  const [searching, setSearching]       = useState(false);
  const [allResults, setAllResults]     = useState<ArticleResult[]>([]);
  const [results, setResults]           = useState<ArticleResult[]>([]);
  const [displayCount, setDisplayCount] = useState(0);
  const [searchDone, setSearchDone]     = useState(false);
  const [searchTime, setSearchTime]     = useState<number | null>(null);
  const [activeSlug, setActiveSlug]     = useState<string>('');
  const resultsRef = useRef<HTMLDivElement>(null);

  const doSearch = useCallback(async (q: string, slug?: string) => {
    if (!q.trim()) return;
    setSearching(true);
    setSearchDone(false);
    setAllResults([]);
    setResults([]);
    setDisplayCount(0);
    const t0 = performance.now();
    try {
      const body: Record<string, unknown> = { query: q, limit: 60 };
      if (slug) body.slug_code = slug;

      const res  = await fetch('/api/articles/search', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await res.json();
      const fetched: ArticleResult[] = data.results || [];
      setAllResults(fetched);
      setResults(fetched.slice(0, DISPLAY_STEP));
      setDisplayCount(DISPLAY_STEP);
      setSearchTime(Math.round(performance.now() - t0));
      setSearchDone(true);
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    } catch (e) {
      console.error('articles search error:', e);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    doSearch(query, activeSlug || undefined);
  };

  const handleExample = (q: string) => {
    setQuery(q);
    doSearch(q, activeSlug || undefined);
  };

  const handleCodeFilter = (slug: string) => {
    const next = activeSlug === slug ? '' : slug;
    setActiveSlug(next);
    if (query.trim()) doSearch(query, next || undefined);
  };

  const handleReset = () => {
    setQuery('');
    setAllResults([]);
    setResults([]);
    setSearchDone(false);
    setSearchTime(null);
    setDisplayCount(0);
    setActiveSlug('');
  };

  const loadMore = () => {
    setDisplayCount(prev => {
      const next = Math.min(prev + DISPLAY_STEP, allResults.length);
      setResults(allResults.slice(0, next));
      return next;
    });
  };

  return (
    <>
      <Helmet>
        <title>{t('articles.search.meta_title')}</title>
        <meta name="description" content={t('articles.search.meta_description')} />
        <link rel="canonical" href={`${SITE_URL}/articles`} />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />

        <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-10">

          {/* Breadcrumb */}
          <nav className="text-xs text-muted-foreground mb-6 flex items-center gap-1.5">
            <Link to="/" className="hover:text-foreground transition-colors">{t('articles.home')}</Link>
            <span className="text-muted-foreground/40">/</span>
            <span className="text-foreground">{t('articles.title')}</span>
          </nav>

          {/* ── En-tête épuré ── */}
          <div className="mb-6">
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="text-3xl">⚖️</span>
                <div>
                  <p className="text-[10px] font-bold tracking-widest uppercase text-primary">{t('articles.search.eyebrow')}</p>
                  <h1 className="text-xl sm:text-2xl font-bold text-foreground leading-tight">{t('articles.title')}</h1>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                <Link to="/browse" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">⚖ {t('nav.jurisprudence')}</Link>
                <span className="text-border">·</span>
                <Link to="/reglements" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">🇪🇺 {t('reglements.breadcrumb')}</Link>
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/60 border border-border">
                <span className="text-base font-bold text-foreground">35+</span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('articles.search.stat_codes')}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/60 border border-border">
                <span className="text-base font-bold text-foreground">400k+</span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('articles.search.stat_articles')}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/60 border border-border">
                <span className="text-base font-bold text-foreground">{t('articles.search.stat_ai')}</span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('articles.search.stat_semantic')}</span>
              </div>
            </div>
          </div>

          {/* ── Zone de recherche unifiée ── */}
          <div className="rounded-2xl border border-border bg-muted/40 px-5 sm:px-6 pt-4 pb-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[13px] font-bold text-foreground">
                <span className="text-lg leading-none">⚖</span>{' '}
                {t('articles.search.box_title')}
              </p>
            </div>
            <div className="h-px bg-border/60 mb-3" />

            <form onSubmit={handleSubmit} className="flex gap-2">
              <div className="flex-1 flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-2.5 focus-within:ring-2 focus-within:ring-primary/30 transition-all">
                <svg className="h-4 w-4 text-muted-foreground shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={t('articles.search.placeholder')}
                  className="flex-1 bg-transparent outline-none text-[14px] placeholder:text-muted-foreground/60"
                />
                {query && (
                  <button type="button" onClick={handleReset} className="text-muted-foreground hover:text-foreground transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <button type="submit" disabled={searching || !query.trim()}
                className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-[13px] hover:bg-primary/90 disabled:opacity-50 transition-colors shrink-0">
                {searching ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('articles.search_submit')}
              </button>
            </form>

            {/* Filtres codes */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              {FEATURED_CODES.map(c => (
                <button key={c.slug} onClick={() => handleCodeFilter(c.slug)}
                  className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                    activeSlug === c.slug ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-muted-foreground hover:border-primary/40'
                  }`}>
                  {t(c.labelKey)}
                </button>
              ))}
            </div>

            {/* Exemples de recherche */}
            <div className="mt-4 pt-3 border-t border-border/60">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground/50 mb-2">{t('articles.search.examples_title')}</p>
              <div className="flex flex-wrap gap-2">
                {QUICK_EXAMPLES.map(ex => (
                  <button key={ex.labelKey} onClick={() => handleExample(ex.query)}
                    className="px-3 py-1.5 rounded-lg border border-border bg-background text-[12px] text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all">
                    {t(ex.labelKey)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── Résultats ── */}
          <div ref={resultsRef}>
            {searching && (
              <div className="flex flex-col items-center py-24 gap-4 text-muted-foreground">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm">{t('articles.semantic_searching')}</p>
              </div>
            )}

            {!searching && searchDone && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    <Trans t={t} i18nKey="articles.results_for" count={allResults.length} components={{
                      strong: <strong className="text-foreground">{allResults.length}</strong>,
                      q: <em className="text-foreground">{t('articles.quoted', { query })}</em>,
                    }} />
                    {searchTime != null && (
                      <span className="ml-2 text-xs text-muted-foreground/60">
                        ({searchTime < 1000 ? `${searchTime} ms` : `${(searchTime / 1000).toFixed(2)} s`})
                      </span>
                    )}
                  </p>
                  <button
                    onClick={handleReset}
                    className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    {t('articles.new_search')}
                  </button>
                </div>

                {results.length === 0 ? (
                  <div className="text-center py-24 text-muted-foreground">
                    <p className="text-5xl mb-4">🔎</p>
                    <p className="font-semibold text-foreground mb-1">{t('articles.no_article_found')}</p>
                    <p className="text-sm">{t('articles.search.no_results_hint')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {results.map(r => <ArticleCard key={r.id} r={r} />)}
                    {displayCount < allResults.length && (
                      <div className="flex justify-center pt-4">
                        <button
                          onClick={loadMore}
                          className="flex items-center gap-2 px-6 py-2.5 rounded-xl border-2 border-border hover:border-primary/40 hover:bg-accent text-sm font-medium transition-all"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          {t('articles.see_more_remaining', { remaining: allResults.length - displayCount })}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>

        <PublicFooter />
      </div>
      <MCPBadge />
    </>
  );
}
